/**
 * "Needs your attention": everything an owner has to DO, ranked. THE PLAN, §4 P4, and ease
 * package P-5.9 ("nothing breaks silently").
 *
 * THE QUEUE DOES NOT CARRY THE BALANCE, AND THAT IS THE POINT OF ITS PROMISE. Billing is
 * manual, so a low balance has no action in this product; `CreditBalanceCard` owns it alone.
 *
 * AN ABSENCE IS NOT AN ALL-CLEAR. This returns `{ rows, unchecked }` rather than an array: a
 * source that could not be read is NAMED, and the "nothing needs you" sentence is reached only
 * when every source is known. A caller passes `null` for a read that failed or has not landed,
 * never an empty stand-in.
 *
 * FIVE SOURCES. The channels (`getInboxes`), the server's Needs-you rows (`status.needsYou`:
 * dead menu options, out-of-date forms, failed sources with a reason CODE, sources that taught
 * nothing), the connections' health (P-3.5), the questions it could not answer, and the tasks
 * using a switched-off connection (P-10.21). The failed-source row used to come from the jobs
 * list with the ingester's own words in it; it is the server's now, with a code (P-5.6).
 *
 * Free of Vue and of copy: this decides WHAT is wrong, how bad and in what order;
 * `AttentionRow.vue` decides how to say it.
 */

/**
 * KEY ORDER IS RANK ORDER. `overview.spec.js` asserts the rendered rows against
 * `Object.values(ATTENTION_KIND)`, so a new kind goes where its severity and rank put it.
 */
export const ATTENTION_KIND = {
  /** A WhatsApp channel with no assistant: nothing there will ever answer. */
  DISCONNECTED_INBOX: 'disconnected_inbox',
  /** P-5.9: a menu option opens a task that no longer exists. A dead end. */
  MENU_TASK_MISSING: 'menu_task_missing',
  /** P-5.9: a published form was built for an older version of its task. A dead end. */
  FORM_OUT_OF_DATE: 'form_out_of_date',
  /**
   * Field fixes G1: WhatsApp paused a form (throttled or blocked). The first GAP: the chat asks
   * its questions meanwhile, and WhatsApp lifts the pause by itself.
   */
  FORM_PAUSED: 'form_paused',
  /** P-1.3: nobody is chosen for the chats it passes on, so nobody is alerted. */
  HANDOVER_UNSET: 'handover_unset',
  /** A knowledge source that could not be read; none of it was saved. */
  FAILED_SOURCE: 'failed_source',
  /** P-5.9: a source that was read and taught the assistant nothing. */
  SOURCE_TAUGHT_NOTHING: 'source_taught_nothing',
  /** A live connection that keeps failing (P-3.5). The only failing-connection row. */
  FAILING_CONNECTION: 'failing_connection',
  /** A task that is on, using a connection that is switched off (P-10.21). */
  TASK_CONNECTION_OFF: 'task_connection_off',
  /** P-5.9: a menu option opens a task that is switched off (customers go to the team). */
  MENU_TASK_OFF: 'menu_task_off',
  /** A question a customer asked that the assistant could not answer. */
  UNANSWERED: 'unanswered',
  /** P-4.6: WhatsApp refused a message she sent for approval. Its verb opens it in the editor. */
  TEMPLATE_REFUSED: 'template_refused',
  /** P-4.6: WhatsApp paused one; broadcasts cannot use it until it is back. */
  TEMPLATE_PAUSED: 'template_paused',
  /**
   * P-1.7: answers the team suggested, waiting on the owner. The last GAP: nothing is broken.
   * It stays above `TEMPLATE_APPROVED`, the one NEWS kind, or the rendered order (severity
   * first) would stop matching this key order.
   */
  TEAM_SUGGESTIONS: 'team_suggestions',
  /**
   * Ease C3 (P-6.9): a "For now" note whose last day is tomorrow. A GAP, the last one: it is
   * still true today, and the verb (Extend it) is hers to take or leave.
   */
  NOTICE_ENDING: 'notice_ending',
  /** P-4.6: WhatsApp approved one. Good news with a verb: use it in a broadcast. */
  TEMPLATE_APPROVED: 'template_approved',
};

export const ATTENTION_SOURCE = {
  INBOXES: 'inboxes',
  NEEDS_YOU: 'needs_you',
  CONNECTIONS: 'connections',
  ACTIVITY: 'activity',
  TASKS: 'tasks',
  TEMPLATES: 'templates',
};

/**
 * ONE COLOUR AXIS. STOPPED: customers are stuck there RIGHT NOW. GAP: it still answers, but
 * it is missing something. The list is sorted by it, so the colours never interleave.
 * NEWS (P-4.6) is good news with a verb: neutral grey, always below every problem.
 */
export const ATTENTION_LEVEL = {
  STOPPED: 'stopped',
  GAP: 'gap',
  /**
   * P-4.6: something good that still has a verb (WhatsApp approved her message). Neutral, never
   * the accent, and always below every problem, so red above amber above grey stays readable.
   */
  NEWS: 'news',
};

/** Ordered worst-first. The index is the primary sort key; see `LEVEL_OF`. */
const LEVEL_ORDER = [
  ATTENTION_LEVEL.STOPPED,
  ATTENTION_LEVEL.GAP,
  ATTENTION_LEVEL.NEWS,
];

const LEVEL_OF = {
  [ATTENTION_KIND.DISCONNECTED_INBOX]: ATTENTION_LEVEL.STOPPED,
  [ATTENTION_KIND.MENU_TASK_MISSING]: ATTENTION_LEVEL.STOPPED,
  [ATTENTION_KIND.FORM_OUT_OF_DATE]: ATTENTION_LEVEL.STOPPED,
  // G1: customers are still served, in the chat: a gap.
  [ATTENTION_KIND.FORM_PAUSED]: ATTENTION_LEVEL.GAP,
  // P-1.3: it still answers, but nobody is alerted about what it passes on: a gap.
  [ATTENTION_KIND.HANDOVER_UNSET]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.FAILED_SOURCE]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.SOURCE_TAUGHT_NOTHING]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.FAILING_CONNECTION]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.TASK_CONNECTION_OFF]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.MENU_TASK_OFF]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.UNANSWERED]: ATTENTION_LEVEL.GAP,
  // A refusal or a pause leaves her with a message broadcasts cannot use: a gap, not a stop.
  [ATTENTION_KIND.TEMPLATE_REFUSED]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.TEMPLATE_PAUSED]: ATTENTION_LEVEL.GAP,
  // P-1.7: a to-do for her, not a fault.
  [ATTENTION_KIND.TEAM_SUGGESTIONS]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.NOTICE_ENDING]: ATTENTION_LEVEL.GAP,
  [ATTENTION_KIND.TEMPLATE_APPROVED]: ATTENTION_LEVEL.NEWS,
};

/** The tiebreak within a severity: the key order above. */
const RANK = Object.fromEntries(
  Object.values(ATTENTION_KIND).map((kind, index) => [kind, index])
);

/** The BFF's verdict word → the row it becomes, in rank order. */
const TEMPLATE_ROWS = [
  { event: 'refused', kind: ATTENTION_KIND.TEMPLATE_REFUSED },
  { event: 'paused', kind: ATTENTION_KIND.TEMPLATE_PAUSED },
  { event: 'approved', kind: ATTENTION_KIND.TEMPLATE_APPROVED },
];

/** Per-kind cap, so one bad afternoon cannot bury the other kinds. */
const MAX_PER_KIND = 3;
/**
 * Total ceiling: one row of every kind fits (16 kinds, with P-1.3, P-1.7, C3's P-6.9 and field
 * fixes G1's paused form), and past this it is a report, not a queue. `overview.spec.js` mounts
 * one row per kind, so a new kind raises this in the same change.
 */
const MAX_ROWS = 16;

const isFilled = value => value !== null && value !== undefined;

const MAX_NAME = 80;
const connectionName = connection => {
  const words =
    String(connection.description ?? '').trim() ||
    String(connection.name ?? '');
  return words.length > MAX_NAME ? `${words.slice(0, MAX_NAME - 1)}…` : words;
};

/**
 * A channel with no assistant is a row only when it is WhatsApp (P-5.5 lists every Chatwoot
 * inbox now, and a website widget without one is a choice), or when an older BFF did not say
 * what it is.
 */
const askable = inbox =>
  !inbox.connected &&
  (inbox.channelType === null ||
    inbox.channelType === undefined ||
    inbox.channelType === 'Channel::Whatsapp');

/** One server Needs-you row (`status.needsYou`) as a queue row. */
const NEEDS_ROW = {
  menu_task_missing: row => ({
    kind: ATTENTION_KIND.MENU_TASK_MISSING,
    menuId: row.subject.id,
    title: row.subject.title,
  }),
  menu_task_off: row => ({
    kind: ATTENTION_KIND.MENU_TASK_OFF,
    menuId: row.subject.id,
    title: row.subject.title,
  }),
  form_out_of_date: row => ({
    kind: ATTENTION_KIND.FORM_OUT_OF_DATE,
    taskId: row.subject.id,
    title: row.subject.title,
  }),
  form_paused: row => ({
    kind: ATTENTION_KIND.FORM_PAUSED,
    taskId: row.subject.id,
    title: row.subject.title,
  }),
  source_failed: row => ({
    kind: ATTENTION_KIND.FAILED_SOURCE,
    title: row.subject.title,
    // A CODE. `AttentionRow` says it with `WASLO.ANSWERS.REASON.*` (P-5.6).
    reason: row.reason ?? 'unknown',
  }),
  source_taught_nothing: row => ({
    kind: ATTENTION_KIND.SOURCE_TAUGHT_NOTHING,
    title: row.subject.title,
    documentId: row.documentId,
  }),
};

/**
 * @param {object} sources
 * @param {?Array<{id:number, name:string, phoneNumber:?string, connected:boolean, channelType?:?string}>} sources.inboxes
 * @param {?Array<{code:string, subject:{kind:string,id:number,title:string}, fix:string, reason:?string, documentId:?number}>} sources.needsYou
 * @param {?{unanswered:Array<{query:string, count:number}>}} sources.activity
 * @param {?Array<{id:number, name:string, description:string, enabled:boolean, health:?{state:string, failed:number}}>} sources.connections
 * @param {?{off:Array<{taskId:number, title:string, connection:string}>}} sources.connectionUse
 * @param {?Array<{id:number, kind:string, inboxId:?number, templateId:string, name:string, language:string, reason:?string}>} sources.templateEvents
 * @param {{set:boolean}|null|undefined} [sources.handover] P-1.3. `undefined`/`null`: cannot tell.
 * @param {{pending:number}|null|undefined} [sources.suggestions] P-1.7. `undefined`/`null`: not read.
 * @param {?Array<{id:number, text:string, endsOn:string, endsTomorrow:boolean}>} [sources.notices]
 *   C3 P-6.9. A to-do like P-1.7: `null` (not read) is no row and is never named as unchecked.
 * @returns {{rows: Array<object>, unchecked: string[]}}
 */
export const buildAttentionQueue = ({
  inboxes = null,
  needsYou = null,
  activity = null,
  connections = null,
  connectionUse = null,
  templateEvents = null,
  handover = null,
  suggestions = null,
  notices = null,
} = {}) => {
  const rows = [];
  const unchecked = [];

  // P-1.3 and P-1.7 are TO-DOs, not faults: an answer we could not get is no row, and is
  // never named as unchecked (nothing is broken because a count could not be read).
  if (handover?.set === false) {
    rows.push({ id: 'handover:unset', kind: ATTENTION_KIND.HANDOVER_UNSET });
  }
  if (Number.isSafeInteger(suggestions?.pending) && suggestions.pending > 0) {
    rows.push({
      id: 'team-suggestions',
      kind: ATTENTION_KIND.TEAM_SUGGESTIONS,
      count: suggestions.pending,
    });
  }
  if (Array.isArray(notices)) {
    notices
      .filter(notice => notice.endsTomorrow === true)
      .slice(0, MAX_PER_KIND)
      .forEach(notice => {
        rows.push({
          id: `notice:${notice.id}`,
          kind: ATTENTION_KIND.NOTICE_ENDING,
          noticeId: notice.id,
          text: notice.text,
        });
      });
  }

  if (isFilled(inboxes)) {
    inboxes
      .filter(askable)
      .slice(0, MAX_PER_KIND)
      .forEach(inbox => {
        rows.push({
          id: `inbox:${inbox.id}`,
          kind: ATTENTION_KIND.DISCONNECTED_INBOX,
          // A NUMBER beside the row key: this row's action is a request about THIS channel.
          inboxId: inbox.id,
          name: inbox.name,
          phoneNumber: inbox.phoneNumber,
        });
      });
  } else {
    unchecked.push(ATTENTION_SOURCE.INBOXES);
  }

  if (isFilled(needsYou)) {
    needsYou.forEach(row => {
      const build = NEEDS_ROW[row.code];
      if (typeof build !== 'function') return;
      rows.push({ id: `needs:${row.code}:${row.subject.id}`, ...build(row) });
    });
  } else {
    unchecked.push(ATTENTION_SOURCE.NEEDS_YOU);
  }

  if (isFilled(connections)) {
    connections
      .filter(
        connection =>
          connection.enabled === true && connection.health?.state === 'failing'
      )
      .slice()
      .sort((a, b) => b.health.failed - a.health.failed)
      .slice(0, MAX_PER_KIND)
      .forEach(connection => {
        rows.push({
          id: `connection:${connection.id}`,
          kind: ATTENTION_KIND.FAILING_CONNECTION,
          toolId: connection.id,
          name: connectionName(connection),
          count: connection.health.failed,
        });
      });
  } else {
    unchecked.push(ATTENTION_SOURCE.CONNECTIONS);
  }

  if (isFilled(activity)) {
    activity.unanswered.slice(0, MAX_PER_KIND).forEach((item, index) => {
      rows.push({
        // Keyed by the group when there is one, so a reload that drops a fixed question
        // never hands its row (and its place in the list) to the next one. An older BFF
        // sends no group: position is all there is.
        id:
          item.id === null || item.id === undefined
            ? `unanswered:${index}`
            : `unanswered:gap:${item.id}`,
        kind: ATTENTION_KIND.UNANSWERED,
        query: item.query,
        count: item.count,
        // C3 P-6.6: the group of wordings, so "Fix it" clears every one of them.
        gapId: item.id ?? null,
      });
    });
  } else {
    unchecked.push(ATTENTION_SOURCE.ACTIVITY);
  }

  if (isFilled(connectionUse)) {
    connectionUse.off.slice(0, MAX_PER_KIND).forEach(item => {
      rows.push({
        id: `task-connection:${item.taskId}:${item.connection}`,
        kind: ATTENTION_KIND.TASK_CONNECTION_OFF,
        title: item.title,
        connection: item.connection,
      });
    });
  } else {
    unchecked.push(ATTENTION_SOURCE.TASKS);
  }

  // P-4.6: WhatsApp's verdicts, each carrying what its verb needs (the event to mark seen, the
  // template and the number to open it on). A read that failed is named, never zero rows.
  if (isFilled(templateEvents)) {
    TEMPLATE_ROWS.forEach(({ event, kind }) => {
      templateEvents
        .filter(item => item.kind === event)
        .slice(0, MAX_PER_KIND)
        .forEach(item => {
          rows.push({
            id: `template-event:${item.id}`,
            kind,
            eventId: item.id,
            inboxId: item.inboxId,
            templateId: item.templateId,
            name: item.name,
            language: item.language,
            reason: item.reason,
          });
        });
    });
  } else {
    unchecked.push(ATTENTION_SOURCE.TEMPLATES);
  }

  // Severity first, so the colour column can never interleave; the day-cost rank only
  // ever breaks ties inside one colour.
  rows.forEach(row => {
    row.level = LEVEL_OF[row.kind];
  });
  rows.sort(
    (a, b) =>
      LEVEL_ORDER.indexOf(a.level) - LEVEL_ORDER.indexOf(b.level) ||
      RANK[a.kind] - RANK[b.kind]
  );

  return { rows: rows.slice(0, MAX_ROWS), unchecked };
};

export default buildAttentionQueue;
